import { useEffect, useRef, useState } from "react";
import { api, type TripView } from "./api.ts";
import { inviteText } from "./inviting.ts";
import { sheetAlwaysOpens } from "./shareSheet.ts";
import { Button, Icon } from "./souvenir.ts";
import { CopyButton, CopyLink } from "./ui.tsx";

// Bring the others in (features/getting-started.md): the owner came back on a later day to a trip worth
// sharing that still has only them. Invite hands on the join link; Just me puts it away for good on this
// trip, the X for a week, for this person on every device. Where the trip's notices go, below a new
// version or changes.
export function InviteCard({ trip, onPutAway }: { trip: TripView; onPutAway: () => void }) {
  const { invite, copied } = useInvite(trip.title, trip.join_link);
  // Gone at once; if it doesn't reach Tripmo, it comes back next time, which is no harm.
  const putAway = (how: "for_good" | "week") => {
    onPutAway();
    api.putAwayInvite(trip.id, how).catch(() => {});
  };
  return (
    <aside className="tm-invite" aria-label="Bring the others in">
      <div className="tm-invite-text">
        <h2 className="callout tm-invite-title">Planning this with others?</h2>
        <p className="caption tm-muted tm-invite-why">
          {/* Where it wraps, it wraps after the colon. */}
          Send them the link: <span className="tm-invite-how">they ask to join, you let them in.</span>
        </p>
        <div className="tm-invite-actions">
          <Button variant="primary" size="sm" icon={copied ? "check" : "share"} onClick={invite}>
            {copied ? "Link copied" : "Invite"}
          </Button>
          <Button variant="quiet" size="sm" onClick={() => putAway("for_good")}>
            Just me
          </Button>
        </div>
      </div>
      <button type="button" className="tm-card-close" aria-label="Not now" onClick={() => putAway("week")}>
        <Icon name="close" size={18} />
      </button>
    </aside>
  );
}

// The join link on People: wherever the browser has a share sheet (a phone's, or a computer's: below),
// Invite opens it, with Copy beside it; where there's none, the link with Copy.
export function InviteLink({ title, link }: { title: string; link: string }) {
  const { invite, copied } = useInvite(title, link);
  if (!hasShareSheet()) return <CopyLink url={link} />;
  return (
    <div className="tm-invite-link">
      <div className="tm-copy">
        <code className="callout">{link}</code>
      </div>
      <div className="tm-invite-actions">
        <Button variant="primary" size="sm" icon={copied ? "check" : "share"} onClick={invite}>
          {copied ? "Link copied" : "Invite"}
        </Button>
        <CopyButton text={link} />
      </div>
    </div>
  );
}

// Whether the browser has a share sheet Tripmo opens (features/map.md, "The share sheet wherever there is
// one"): a phone's, and since 2026-10-30 a computer's where its browser has one that always opens (Safari on a
// Mac; Chrome and Edge on Windows and ChromeOS), never Firefox's or Linux's, which have none, nor Chrome's,
// Edge's or any Chromium browser's on a Mac, whose tip can land off the page (`sheetAlwaysOpens` in
// shareSheet.ts). The one rule for Share and Invite, apart from whether the layout is a phone's (TripPage.tsx's
// `wide`): that decides how Share looks, this whether it opens a sheet. `data` is what would be shared, which
// the browser may refuse (`canShare`).
export function hasShareSheet(data: ShareData = { url: location.href }): boolean {
  const agent = (navigator as Navigator & { userAgentData?: { brands: readonly { brand: string }[] } }).userAgentData;
  return (
    typeof navigator.share === "function" &&
    (navigator.canShare?.(data) ?? true) &&
    sheetAlwaysOpens({
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints,
      brands: agent ? agent.brands.map((b) => b.brand) : null,
    })
  );
}

// Invite: the share sheet with the words and the join link, else the link copied, and for a moment the
// page says so ("copied"). Cancelling the share sheet is nothing to report.
export function useInvite(title: string, link: string): { invite: () => Promise<void>; copied: boolean } {
  const { share, copied } = useShareLink(inviteText(title), link);
  return { invite: share, copied };
}

// Sending a link on, for Invite's join link and a place's Share (ShareItem.tsx). `share`: the share sheet
// with the words and the link wherever there is one (`sheet`), else, or where it refuses, the link copied.
// `copy`: the link copied, never the sheet (a computer's "Copy link" beside Share: a Mac's sheet may offer no
// copy). Either copy sets `copied` for 4 seconds, one state for both, so the page says "Link copied" in one
// place at a time. Cancelling the share sheet is nothing to report, and while it's open, Share again is
// nothing too (the browser refuses a second sheet), never a copy.
export function useShareLink(
  text: string,
  link: string,
): { share: () => Promise<void>; copy: () => Promise<void>; copied: boolean; sheet: boolean } {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const data = { text, url: link };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // No clipboard here (a page not served securely): nothing to say it did.
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 4000);
  };
  const share = async () => {
    if (hasShareSheet(data)) {
      try {
        await navigator.share(data);
        return;
      } catch (e) {
        // Closed without sharing, or Share again while its sheet is open: nothing to report.
        const name = (e as Error).name;
        if (name === "AbortError" || name === "InvalidStateError") return;
        // The share sheet refused (no permission, nothing to share to): copy instead.
      }
    }
    await copy();
  };
  return { share, copy, copied, sheet: hasShareSheet(data) };
}
